import { ReactNode } from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface ToggleButtonWithCheckboxProps {
  children: ReactNode;
  className?: string;
  selected: boolean;
  toggle: () => void;
}

export function ToggleButtonWithCheckbox({ children, className, selected, toggle }: ToggleButtonWithCheckboxProps) {
  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-4 rounded-lg bg-background p-4 text-sm font-medium shadow-md hover:bg-accent',
        className
      )}
      onClick={() => toggle()}
    >
      <Checkbox checked={selected} />
      {children}
    </div>
  );
}

const toggleButtonVariants = cva(
  'bg-background hover:bg-accent cursor-pointer rounded-md border p-3 text-sm font-medium data-[selected=true]:bg-accent data-[selected=true]:border-primary',
  {
    variants: {
      layout: {
        default: 'flex items-center gap-1.5',
        vertical: 'flex flex-col items-center gap-1.5',
      },
    },
  }
);

type ToggleButtonProps = {
  selected: boolean;
  toggle: () => void;
  children: ReactNode;
  className?: string;
} & VariantProps<typeof toggleButtonVariants>;

export function ToggleButton({ children, className, selected, toggle, layout = 'default' }: ToggleButtonProps) {
  return (
    <div data-selected={selected} className={cn(toggleButtonVariants({ layout }), className)} onClick={() => toggle()}>
      {children}
    </div>
  );
}
